import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import FitScreenOutlined from "@mui/icons-material/FitScreenOutlined";
import HeightOutlined from "@mui/icons-material/HeightOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import ZoomInOutlined from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlined from "@mui/icons-material/ZoomOutOutlined";
import { Box, IconButton, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { useState } from "react";

import { clampZoom, MAX_ZOOM, MIN_ZOOM } from "./zoom";

const ZOOM_STEP = 0.1;

export interface PreviewSearchControls {
  query: string;
  onQueryChange: (query: string) => void;
  matchCount: number;
  activeIndex: number | null;
  onNext: () => void;
  onPrevious: () => void;
}

interface PreviewZoomBarProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onFitWidth?: () => void;
  onFitHeight?: () => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  search?: PreviewSearchControls;
  allowPageInput?: boolean;
}

function SearchControls({ search }: { search: PreviewSearchControls }) {
  const matchLabel =
    search.matchCount === 0 || search.activeIndex == null
      ? "0 of 0"
      : `${search.activeIndex + 1} of ${search.matchCount}`;

  // Grows from 0 but never below min-content (search at 80px), so the bar wraps only
  // once search has shrunk; max-content stops growth at the 160px search box.
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        flexWrap: "nowrap",
        flex: "1 1 0px",
        minWidth: "min-content",
        maxWidth: "max-content",
      }}
    >
      <TextField
        size="small"
        placeholder="Search"
        value={search.query}
        onChange={(event) => search.onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          if (event.shiftKey) search.onPrevious();
          else search.onNext();
        }}
        slotProps={{ htmlInput: { "aria-label": "Search document" } }}
        sx={{ flex: "1 1 80px", minWidth: 80, maxWidth: 160 }}
      />
      {search.query.trim() !== "" && (
        <Typography
          variant="caption"
          sx={{ minWidth: 48, textAlign: "center", userSelect: "none", whiteSpace: "nowrap" }}
        >
          {matchLabel}
        </Typography>
      )}
      <Tooltip title="Previous match">
        <span>
          <IconButton
            size="small"
            aria-label="Previous match"
            onClick={search.onPrevious}
            disabled={search.matchCount === 0}
          >
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next match">
        <span>
          <IconButton
            size="small"
            aria-label="Next match"
            onClick={search.onNext}
            disabled={search.matchCount === 0}
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function ZoomControls({
  zoom,
  onZoomChange,
  onFitWidth,
  onFitHeight,
}: Pick<PreviewZoomBarProps, "zoom" | "onZoomChange" | "onFitWidth" | "onFitHeight">) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.25, flexWrap: "nowrap", flexShrink: 0 }}
    >
      <Tooltip title="Zoom out">
        <span>
          <IconButton
            size="small"
            onClick={() => onZoomChange(clampZoom(zoom - ZOOM_STEP))}
            disabled={zoom <= MIN_ZOOM}
          >
            <ZoomOutOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Typography
        variant="caption"
        sx={{ minWidth: 40, textAlign: "center", userSelect: "none", whiteSpace: "nowrap" }}
      >
        {Math.round(zoom * 100)}%
      </Typography>

      <Tooltip title="Zoom in">
        <span>
          <IconButton
            size="small"
            onClick={() => onZoomChange(clampZoom(zoom + ZOOM_STEP))}
            disabled={zoom >= MAX_ZOOM}
          >
            <ZoomInOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {onFitWidth && (
        <Tooltip title="Fit to window">
          <IconButton size="small" onClick={onFitWidth}>
            <FitScreenOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {onFitHeight && (
        <Tooltip title="Fit height">
          <IconButton size="small" onClick={onFitHeight}>
            <HeightOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function PageNumberInput({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  // null means "not editing": the input shows currentPage until the person types.
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    // A number input's value never contains commas; Number.isInteger below rejects decimals.
    // ast-grep-ignore: no-unsafe-number-parsing
    const parsed = Number(draft);
    if (Number.isInteger(parsed) && parsed >= 1 && parsed <= totalPages) {
      onPageChange(parsed - 1);
    }
    setDraft(null);
  };

  return (
    <>
      <TextField
        size="small"
        value={draft ?? String(currentPage + 1)}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          // Keeps Enter from implicitly submitting a surrounding form.
          event.preventDefault();
          commit();
        }}
        onBlur={commit}
        slotProps={{
          htmlInput: { type: "number", min: 1, max: totalPages, "aria-label": "Page number" },
        }}
        sx={{ width: 56, flexShrink: 0 }}
      />
      <Typography variant="caption" sx={{ userSelect: "none", whiteSpace: "nowrap" }}>
        / {totalPages}
      </Typography>
    </>
  );
}

export function PreviewZoomBar({
  zoom,
  onZoomChange,
  onFitWidth,
  onFitHeight,
  currentPage,
  totalPages,
  onPageChange,
  search,
  allowPageInput,
}: PreviewZoomBarProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: theme.palette.surfaceSubtle,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 0.5,
        py: 0.25,
        gap: 0.25,
        flexShrink: 0,
        flexWrap: "wrap",
      }}
    >
      {search && <SearchControls search={search} />}

      <ZoomControls
        zoom={zoom}
        onZoomChange={onZoomChange}
        onFitWidth={onFitWidth}
        onFitHeight={onFitHeight}
      />

      {totalPages != null && currentPage != null && onPageChange && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: "auto", flexShrink: 0 }}>
          <Tooltip title="Previous page">
            <span>
              <IconButton
                size="small"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 0}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {allowPageInput ? (
            <PageNumberInput
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{ minWidth: 48, textAlign: "center", userSelect: "none", whiteSpace: "nowrap" }}
            >
              {currentPage + 1} / {totalPages}
            </Typography>
          )}
          <Tooltip title="Next page">
            <span>
              <IconButton
                size="small"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
